// 2) Third-party libraries
import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

// 3.3 Project-specific modules � components/types
import { PageData, SupportTicketsState } from "./types";

// 3.4 Utility functions
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'supportTicket';

// Type-safe initialState!
export const initialState: SupportTicketsState = {
    loading: false,
    errorMessages: [],
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
};

const supportTicketSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        submitSupportTicketRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: DynamicObject;
                    callback?: (response: DynamicObject) => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                payload: DynamicObject,
                callback?: (response: DynamicObject) => void
            ) => ({
                payload: {
                    payload,
                    callback
                }
            })
        },
        submitSupportTicketResponseAction: (
            state
        ) => {
            state.loading = false;
        },
        getSupportTicketDataRequestAction: (
            state
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.loading = true;
        },
        setSupportTicketSelectedIdRequestAction: (
            state
        ) => {
            state.activeData.selectedId = initialState.activeData.selectedId;
        },
        setSupportTicketSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getSupportTicketDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getSupportTicketDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearSupportTicketDetailsDataSuccessAction: (
            state
        ) => {
            state.activeData = { ...initialState.activeData };
        }
    }
});

const { actions, reducer } = supportTicketSlice;

// Actions
export const {
    submitSupportTicketRequestAction,
    submitSupportTicketResponseAction
} = actions;

export const attachSupportTicketRequestAction = createAction(
    `${actionPrefix}/attachSupportTicketRequestAction`,
    function prepare(
        payload: DynamicObject,
        callback?: (response: DynamicObject) => void) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

export const getSupportTicketListsRequestAction = createAction(
    `${actionPrefix}/getSupportTicketListsRequestAction`,
    function prepare(
        payload: DynamicObject,
        callback?: (response: DynamicObject) => void,
        refetchCount: number = 1) {
        return {
            payload: {
                payload,
                callback,
                refetchCount
            }
        };
    }
);

// Selectors
export const getSupportTicketsState = (state: RootState) => state.supportTickets;
export const getSupportTicketData = createSelector(
    getSupportTicketsState,
    (supportTickets) => supportTickets.activeData
);
export const getSupportTicketActiveDataRecord = createSelector(
    getSupportTicketData,
    (activeData) => activeData.record
);
export const getSelectedSupportTicketId = createSelector(
    getSupportTicketData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;